import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { SITE_CONTENT } from "../../constants/siteContent";
import { trackNavigation } from "../../utils/analytics";
import {
  whatsappUrl,
  telUrl,
  mailUrl,
  phoneDisplay,
  responseNote,
  EXTERNAL_LINK_PROPS
} from "../../utils/contact";
import Icon from "../icons/Icon";
import Topo from "../anim/Topo";
import MicroLabel from "../ui/MicroLabel";
import styles from "./Header.module.css";

/*
 * Header — a transparent, fixed bar with NO surface of its own, plus the
 * full-screen paper menu it opens.
 *
 *   bar    two-line wordmark (home) · centred monogram · lime CTA · boxed hamburger
 *   menu   every width gets the same one: giant stacked links, the hovered /
 *          focused item's children beside them (a disclosure per item on
 *          phones), CONNECT, and the lime CTA at the foot.
 *
 * The bar floats over whatever section is under it, so it has to know that
 * surface: every <Section> (and the footer panel) stamps data-tone on itself,
 * this component finds the one under the header line and mirrors it as
 * data-over — ink over paper, bone over olive / void.
 *
 * App.js owns the open state: props isMenuOpen, onToggleMenu, onNavItemClick.
 */

/* ------------------------------------------------------------- content -- */
const BRAND = String(SITE_CONTENT.brand || "");
// TODO(content): where the wordmark breaks into its two lines ("APPIN" / "FIGO").
const BRAND_BREAK = 5;
const BRAND_LINE_ONE = BRAND.slice(0, BRAND_BREAK);
const BRAND_LINE_TWO = BRAND.slice(BRAND_BREAK);
const MONOGRAM = BRAND.charAt(0);
const HOME_LABEL = `${BRAND} — home`;

// TODO(content): UI labels. Case is applied in CSS — keep these in sentence case.
const CTA_LABEL = "Talk to us";
const LABEL_PAGES = "Pages";
const LABEL_CONNECT = "Connect";
const MENU_OPEN_LABEL = "Open navigation menu";
const MENU_CLOSE_LABEL = "Close navigation menu";
/* Visible label is "WhatsApp"; the accessible name has to CONTAIN it (WCAG
   2.5.3), which "Chat on WhatsApp" does. Same rule for the two below: the
   visible number / address is the tail of the accessible name. */
const WHATSAPP_LABEL = "WhatsApp";
const WHATSAPP_A11Y_LABEL = "Chat on WhatsApp";
const CALL_A11Y_PREFIX = "Call";
const EMAIL_A11Y_PREFIX = "Email";

/* Every builder returns "" when the contact block is unconfigured, and the rule
   is to render nothing rather than a dead link — each use below is guarded.
   The "call" opener matches the CTA beside it: a booking intent. */
const WHATSAPP_HREF = whatsappUrl("call");
const PHONE_HREF = telUrl();
const PHONE_NUMBER = phoneDisplay();
const MAIL_HREF = mailUrl();
const MAIL_ADDRESS = (SITE_CONTENT.contact && SITE_CONTENT.contact.email) || "";
const RESPONSE_NOTE = responseNote();
const HOURS_NOTE = (SITE_CONTENT.contact && SITE_CONTENT.contact.hoursNote) || "";

/* ------------------------------------------------------------ plumbing -- */
const MENU_ID = "site-menu";
const CONNECT_LABEL_ID = "site-menu-connect";

/* MUST stay in sync with the 980 / 981px queries in Header.module.css: above it
   an item's children sit in a second column, below it they are an accordion. */
const DESKTOP_QUERY = "(min-width: 981px)";
const HOVER_QUERY = "(hover: hover) and (pointer: fine)";
/* Hover intent: a pointer crossing a row on its way to the second column must
   not swap that column out from under it. */
const HOVER_INTENT_MS = 90;

const TONES = ["paper", "olive", "void"];
/* Within this many px of the top the bar shows its full lockup + the monogram;
   past it the monogram leaves (sections carry their own emblem dead centre)
   and the wordmark tightens — both measured from the reference. */
const TOP_ZONE_PX = 48;

/* Beyond this a child list is set in two columns (desktop only), so the longest
   one — twelve services — stays shorter than the link stack beside it. */
const SUBLIST_SPLIT_AT = 6;

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
const NON_CONTENT_TAGS = ["SCRIPT", "STYLE", "LINK", "NOSCRIPT", "TEMPLATE"];

/* Paths contain slashes, which are awkward in ids — one helper so the id on a
   panel and the id its trigger references can never drift apart. */
const submenuId = (path) => `${MENU_ID}-sub${path.replace(/\//g, "-")}`;

const subRows = (count) => (count > SUBLIST_SPLIT_AT ? Math.ceil(count / 2) : count);

function cx(...values) {
  return values.filter(Boolean).join(" ");
}

function mediaMatches(query) {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(query).matches
  );
}

/* The parent whose section the visitor is in, or null. */
function findActiveParent(items, pathname) {
  const hit = items.find((item) => {
    const children = item.children || [];
    return (
      children.length > 0 &&
      (item.path === pathname || children.some((child) => child.path === pathname))
    );
  });
  return hit ? hit.path : null;
}

/* Visible, tabbable descendants in DOM order. getClientRects() is empty for
   anything under display:none (the collapsed child lists); the visibility test
   drops the bar CTA, which is visibility:hidden while the menu is open. */
function tabbableWithin(root) {
  return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), (node) => {
    if (node.getClientRects().length === 0) {
      return false;
    }
    return window.getComputedStyle(node).visibility !== "hidden";
  });
}

function Header({ isMenuOpen, onToggleMenu, onNavItemClick }) {
  const location = useLocation();
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [over, setOver] = useState("paper");
  const [atTop, setAtTop] = useState(true);
  const headerRef = useRef(null);
  const barRef = useRef(null);
  const menuBtnRef = useRef(null);
  const timersRef = useRef({ hover: 0 });

  /* ONE source of nav truth: SITE_CONTENT.navItems drives the whole menu. Add a
     page there and it appears here. */
  const navItems = SITE_CONTENT.navItems;
  const longestLabel = navItems.reduce((max, item) => Math.max(max, item.label.length), 0);
  const maxSubRows = navItems.reduce(
    (max, item) => Math.max(max, subRows((item.children || []).length)),
    0
  );

  /* App owns the menu state, and its close handler is a fresh identity every
     render. Park the latest one in a ref so the document-level listeners below
     can mount once instead of re-subscribing on every parent render. */
  const closeMenuRef = useRef(onNavItemClick);
  useEffect(() => {
    closeMenuRef.current = onNavItemClick;
  });

  /* Which surface is under the bar. One rAF-throttled pass per scroll / resize
     frame: the last [data-tone] box (DOM order, so the innermost) that straddles
     the bar's centre line wins; nothing there means paper. Never scheduled on a
     hidden tab — becoming visible again re-runs it. Re-subscribed per route so
     the new page is read as soon as it is committed; the body ResizeObserver
     catches late layout shifts (images) that move a boundary without a scroll. */
  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    let frame = 0;

    const evaluate = () => {
      frame = 0;
      const header = headerRef.current;
      const bar = barRef.current;
      if (!header || !bar) {
        return;
      }

      const probeY = bar.getBoundingClientRect().height / 2;
      let tone = "paper";
      document.querySelectorAll("[data-tone]").forEach((node) => {
        if (header.contains(node)) {
          return;
        }
        const rect = node.getBoundingClientRect();
        if (rect.width > 0 && rect.top <= probeY && rect.bottom > probeY) {
          const value = node.getAttribute("data-tone");
          if (TONES.indexOf(value) !== -1) {
            tone = value;
          }
        }
      });

      setOver(tone);
      setAtTop(window.scrollY < TOP_ZONE_PX);
    };

    const schedule = () => {
      if (frame || document.hidden) {
        return;
      }
      frame = window.requestAnimationFrame(evaluate);
    };

    evaluate();
    schedule();

    const passive = { passive: true };
    window.addEventListener("scroll", schedule, passive);
    window.addEventListener("resize", schedule, passive);
    document.addEventListener("visibilitychange", schedule);

    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(schedule);
      resizeObserver.observe(document.body);
    }

    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      window.removeEventListener("scroll", schedule, passive);
      window.removeEventListener("resize", schedule, passive);
      document.removeEventListener("visibilitychange", schedule);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [location.pathname]);

  /* Close on route change — covers back / forward while the menu is open, not
     just clicks on its own links. */
  useEffect(() => {
    closeMenuRef.current();
  }, [location.pathname]);

  /* What the second column shows as the menu opens: the section the visitor is
     in (desktop), nothing otherwise. Phones always open collapsed. It is reset
     on OPEN rather than on close so the column does not blink out from under
     the closing wipe. */
  useEffect(() => {
    if (isMenuOpen) {
      setOpenSubmenu(
        mediaMatches(DESKTOP_QUERY)
          ? findActiveParent(SITE_CONTENT.navItems, location.pathname)
          : null
      );
    }
  }, [isMenuOpen, location.pathname]);

  /* Freeze the page behind the menu. Lenis owns scrolling, so stopping the
     instance is what actually holds position — overflow:hidden on <body> alone
     does not stop it. Always restarts on cleanup, including if the component
     unmounts while the menu is open, so scrolling can never be left dead. */
  useEffect(() => {
    if (!isMenuOpen || typeof window === "undefined") {
      return undefined;
    }

    const lenis = window.__lenis;
    if (lenis && typeof lenis.stop === "function") {
      lenis.stop();
    }

    return () => {
      const current = window.__lenis;
      if (current && typeof current.start === "function") {
        current.start();
      }
    };
  }, [isMenuOpen]);

  /* While the menu is open everything that is not the header leaves the tab
     order, the pointer and the accessibility tree: each sibling of the header —
     and of every ancestor up to <body>, which covers portals — goes inert +
     aria-hidden, and gets back exactly what it had before. */
  useEffect(() => {
    if (!isMenuOpen || typeof document === "undefined") {
      return undefined;
    }

    const header = headerRef.current;
    if (!header) {
      return undefined;
    }

    const touched = [];
    let node = header;
    while (node && node !== document.body && node.parentElement) {
      const current = node;
      Array.prototype.forEach.call(current.parentElement.children, (sibling) => {
        if (sibling === current || NON_CONTENT_TAGS.indexOf(sibling.tagName) !== -1) {
          return;
        }
        touched.push({
          element: sibling,
          inert: sibling.getAttribute("inert"),
          ariaHidden: sibling.getAttribute("aria-hidden")
        });
        sibling.setAttribute("inert", "");
        sibling.setAttribute("aria-hidden", "true");
      });
      node = current.parentElement;
    }

    return () => {
      touched.forEach(({ element, inert, ariaHidden }) => {
        if (inert === null) {
          element.removeAttribute("inert");
        } else {
          element.setAttribute("inert", inert);
        }
        if (ariaHidden === null) {
          element.removeAttribute("aria-hidden");
        } else {
          element.setAttribute("aria-hidden", ariaHidden);
        }
      });
    };
  }, [isMenuOpen]);

  /* Escape closes the menu and hands focus back to the control that opened it,
     so a keyboard user is never dumped at the top of the document (WCAG 2.4.3).
     Tab wraps inside header + menu — the belt to inert's braces, for engines
     that do not implement inert. */
  useEffect(() => {
    if (!isMenuOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape" || event.key === "Esc") {
        event.preventDefault();
        closeMenuRef.current();
        if (menuBtnRef.current) {
          menuBtnRef.current.focus();
        }
        return;
      }

      if (event.key !== "Tab" || !headerRef.current) {
        return;
      }

      const stops = tabbableWithin(headerRef.current);
      if (stops.length === 0) {
        return;
      }

      const first = stops[0];
      const last = stops[stops.length - 1];
      const active = document.activeElement;

      if (!headerRef.current.contains(active)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isMenuOpen]);

  /* The timer store is read in the effect body (not in the cleanup) so the
     cleanup closes over the object, never over a stale ref read. */
  useEffect(() => {
    const timers = timersRef.current;
    return () => window.clearTimeout(timers.hover);
  }, []);

  const isPathActive = (path, children = []) => {
    if (location.pathname === path) {
      return true;
    }

    return children.some((child) => location.pathname === child.path);
  };

  /* A link to the page you are already on changes no route, so RouteScrollReset
     never fires — take the visitor to the top ourselves. Lenis is stopped while
     the menu is open, hence force. */
  const scrollTopIfCurrent = (path) => {
    if (location.pathname !== path) {
      return;
    }
    const lenis = window.__lenis;
    if (lenis && typeof lenis.scrollTo === "function") {
      lenis.scrollTo(0, { immediate: true, force: true });
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  };

  const handleTrackedNav = (item, source) => {
    trackNavigation(item.label, item.path, source);
    scrollTopIfCurrent(item.path);
    onNavItemClick();
  };

  /* A click toggles — except a MOUSE click on an item hover has already opened,
     which would otherwise slam shut the column the pointer just revealed.
     Keyboard activation (detail 0) and touch always toggle. */
  const handleSubmenuToggle = (item, event) => {
    const byMouse = event.detail > 0 && mediaMatches(HOVER_QUERY) && mediaMatches(DESKTOP_QUERY);
    window.clearTimeout(timersRef.current.hover);
    trackNavigation(`${item.label} submenu`, item.path, "header_submenu_toggle");
    setOpenSubmenu((prev) => {
      if (prev === item.path) {
        return byMouse ? prev : null;
      }
      return item.path;
    });
  };

  /* Desktop + a real hover pointer only: on touch, mouseenter is synthesised
     just before click and the pair would open-then-close the item. */
  const handleItemEnter = (item) => {
    if (!mediaMatches(HOVER_QUERY) || !mediaMatches(DESKTOP_QUERY)) {
      return;
    }
    window.clearTimeout(timersRef.current.hover);
    timersRef.current.hover = window.setTimeout(() => {
      setOpenSubmenu(item.path);
    }, HOVER_INTENT_MS);
  };

  const handleItemLeave = () => {
    window.clearTimeout(timersRef.current.hover);
  };

  /* Keyboard focus shows the item's children in the second column (desktop).
     :focus-visible keeps a mouse-down focus out of it — that click is already
     handled above, and opening here first would make it a toggle-shut. */
  const handleItemFocus = (item, event) => {
    if (!mediaMatches(DESKTOP_QUERY)) {
      return;
    }
    let byKeyboard = true;
    try {
      byKeyboard = event.target.matches(":focus-visible");
    } catch (error) {
      byKeyboard = true;
    }
    if (byKeyboard) {
      setOpenSubmenu(item.path);
    }
  };

  return (
    <header
      ref={headerRef}
      className={styles.header}
      /* While the menu is open the bar sits on paper, whatever is behind it. */
      data-over={isMenuOpen ? "paper" : over}
      data-top={atTop || isMenuOpen ? "true" : "false"}
      data-open={isMenuOpen ? "true" : "false"}
    >
      <div className={styles.bar} ref={barRef}>
        <Link
          to="/"
          className={styles.brand}
          aria-label={HOME_LABEL}
          onClick={() => {
            trackNavigation("Home", "/", "header_logo");
            scrollTopIfCurrent("/");
            onNavItemClick();
          }}
        >
          <span className={styles.brandSerif} aria-hidden="true">
            {BRAND_LINE_ONE}
          </span>
          <span className={styles.brandSans} aria-hidden="true">
            {BRAND_LINE_TWO}
          </span>
        </Link>

        <span className={styles.monogram} aria-hidden="true">
          {MONOGRAM}
        </span>

        <div className={styles.actions}>
          {/* THE one primary CTA. It steps aside while the menu is open, where
              the same pill sits at the foot — never two at once, at any width. */}
          <Link
            to="/contact"
            className={`btn ${styles.barCta}`}
            onClick={() => {
              trackNavigation(CTA_LABEL, "/contact", "header_cta");
              onNavItemClick();
            }}
          >
            <Icon name="arrow-up-right" size={18} />
            {CTA_LABEL}
          </Link>
          <button
            type="button"
            ref={menuBtnRef}
            className={`btn btn--box ${styles.menuBtn}`}
            aria-label={isMenuOpen ? MENU_CLOSE_LABEL : MENU_OPEN_LABEL}
            aria-expanded={isMenuOpen}
            aria-controls={MENU_ID}
            onClick={() => {
              trackNavigation(
                isMenuOpen ? "Close Menu" : "Open Menu",
                window.location.pathname,
                "header_menu"
              );
              onToggleMenu();
            }}
          >
            <span className={styles.bars} aria-hidden="true">
              <span />
              <span />
            </span>
          </button>
        </div>
      </div>

      {/* Closed menu = visibility:hidden (CSS), aria-hidden and inert, so it
          leaves the accessibility tree AND the tab order instead of sitting
          there as invisible focus stops. The close wipe still plays: visibility
          flips only after the clip-path transition finishes (see the module).
          data-surface (never data-tone — that is this header's own signal)
          hands the subtree the paper text / accent / focus-ring values. */}
      <div
        id={MENU_ID}
        className={cx(styles.menu, isMenuOpen && styles.open)}
        data-surface="paper"
        aria-hidden={isMenuOpen ? undefined : "true"}
        {...(isMenuOpen ? {} : { inert: "" })}
      >
        <Topo tone="paper" crop="start" />

        {/* Lenis (mounted in App.js) swallows wheel/touch for the whole
            document; data-lenis-prevent is its opt-out for nested scrollers,
            and this is the one that scrolls when the menu outgrows the screen. */}
        <div className={styles.menuScroll} data-lenis-prevent>
          <div className={styles.menuInner}>
            <div className={styles.menuGrid}>
              <nav
                className={styles.nav}
                aria-label="Primary"
                style={{ "--nav-ch": longestLabel, "--sub-rows-max": maxSubRows }}
              >
                <MicroLabel as="p" className={cx(styles.groupLabel, styles.rise)}>
                  {LABEL_PAGES}
                </MicroLabel>

                <ul className={styles.navList}>
                  {navItems.map((item, index) => {
                    const children = item.children || [];
                    const hasChildren = children.length > 0;
                    const panelId = submenuId(item.path);
                    const isOpen = openSubmenu === item.path;
                    const isActive = isPathActive(item.path, children);
                    const labelClass = cx(styles.label, isActive && "scribble");

                    return (
                      <li className={styles.item} style={{ "--i": index + 1 }} key={item.path}>
                        {hasChildren ? (
                          <>
                            <button
                              type="button"
                              className={cx(styles.navLink, styles.rise, isActive && styles.active)}
                              aria-expanded={isOpen}
                              aria-controls={panelId}
                              onClick={(event) => handleSubmenuToggle(item, event)}
                              onMouseEnter={() => handleItemEnter(item)}
                              onMouseLeave={handleItemLeave}
                              onFocus={(event) => handleItemFocus(item, event)}
                            >
                              <span className={labelClass}>{item.label}</span>
                              <Icon name="chevron-down" size={20} className={styles.caret} />
                            </button>

                            {/* A navigation disclosure, NOT an application menu:
                                no role="menu". Always rendered so aria-controls
                                resolves; `hidden` (display:none, enforced in the
                                module) keeps a closed list out of the tab order. */}
                            <div className={styles.subPanel} id={panelId} hidden={!isOpen}>
                              <MicroLabel as="p" className={styles.subTitle} aria-hidden="true">
                                {item.label}
                              </MicroLabel>
                              <ul
                                className={styles.subList}
                                aria-label={`${item.label} pages`}
                                data-cols={children.length > SUBLIST_SPLIT_AT ? "2" : "1"}
                                style={{ "--sub-rows": subRows(children.length) }}
                              >
                                {children.map((child, childIndex) => (
                                  <li key={child.path} style={{ "--j": childIndex }}>
                                    <Link
                                      to={child.path}
                                      className={cx(
                                        "micro",
                                        styles.subLink,
                                        location.pathname === child.path && styles.active
                                      )}
                                      aria-current={
                                        location.pathname === child.path ? "page" : undefined
                                      }
                                      onClick={() => handleTrackedNav(child, "header_submenu")}
                                    >
                                      {child.label}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </>
                        ) : (
                          <Link
                            to={item.path}
                            className={cx(styles.navLink, styles.rise, isActive && styles.active)}
                            aria-current={location.pathname === item.path ? "page" : undefined}
                            onClick={() => handleTrackedNav(item, "header_menu")}
                          >
                            <span className={labelClass}>{item.label}</span>
                          </Link>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </nav>

              {WHATSAPP_HREF || PHONE_HREF || MAIL_HREF ? (
                <div
                  className={cx(styles.connect, styles.rise)}
                  role="group"
                  aria-labelledby={CONNECT_LABEL_ID}
                  style={{ "--i": navItems.length + 1 }}
                >
                  <MicroLabel as="p" id={CONNECT_LABEL_ID} className={styles.groupLabel}>
                    {LABEL_CONNECT}
                  </MicroLabel>
                  <ul className={styles.connectList}>
                    {WHATSAPP_HREF ? (
                      <li>
                        <a
                          href={WHATSAPP_HREF}
                          className={styles.connectLink}
                          aria-label={WHATSAPP_A11Y_LABEL}
                          {...EXTERNAL_LINK_PROPS}
                          onClick={() => {
                            trackNavigation(WHATSAPP_A11Y_LABEL, "whatsapp", "header_menu_whatsapp");
                            onNavItemClick();
                          }}
                        >
                          <span className={styles.label}>{WHATSAPP_LABEL}</span>
                        </a>
                      </li>
                    ) : null}
                    {PHONE_HREF && PHONE_NUMBER ? (
                      <li>
                        <a
                          href={PHONE_HREF}
                          className={styles.connectLink}
                          aria-label={`${CALL_A11Y_PREFIX} ${PHONE_NUMBER}`}
                          onClick={onNavItemClick}
                        >
                          <span className={styles.label}>{PHONE_NUMBER}</span>
                        </a>
                      </li>
                    ) : null}
                    {MAIL_HREF && MAIL_ADDRESS ? (
                      <li>
                        <a
                          href={MAIL_HREF}
                          className={styles.connectLink}
                          aria-label={`${EMAIL_A11Y_PREFIX} ${MAIL_ADDRESS}`}
                          onClick={onNavItemClick}
                        >
                          <span className={styles.label}>{MAIL_ADDRESS}</span>
                        </a>
                      </li>
                    ) : null}
                  </ul>
                  {RESPONSE_NOTE ? <p className={styles.connectNote}>{RESPONSE_NOTE}</p> : null}
                </div>
              ) : null}
            </div>

            <div className={cx(styles.menuFoot, styles.rise)} style={{ "--i": navItems.length + 2 }}>
              <Link
                to="/contact"
                className={`btn ${styles.menuCta}`}
                onClick={() => {
                  trackNavigation(CTA_LABEL, "/contact", "header_menu_cta");
                  scrollTopIfCurrent("/contact");
                  onNavItemClick();
                }}
              >
                <Icon name="arrow-up-right" size={18} />
                {CTA_LABEL}
              </Link>
              {HOURS_NOTE ? (
                <MicroLabel as="p" className={styles.footNote}>
                  {HOURS_NOTE}
                </MicroLabel>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
